import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useTransition, animated } from 'react-spring'
import styled from 'styled-components'

import { Company } from '../models/client/company'
import { selectUser } from '../store/user'
import { color } from '../styles'
import { SPACING, TRANSITION_TIME_MD_MS } from '../styles/mixins/constants'
import { isEqual } from '../util/isEqual'
import { insertTestingSelector } from '../util/testing'

import { Logo } from './Icon/SVG'
import { MenuButton } from './MenuButton'
import { NavbarContents } from './Navbar'
import { Spacer } from './Spacer'

type Props = {
  onLogout: () => void
  company: Company | null
}

export const NavbarMobile = (props: Props): JSX.Element => {
  const { onLogout, company } = props
  const [menuVisible, setMenuVisible] = useState(false)
  const user = useSelector(selectUser, isEqual)
  const transitions = useTransition(menuVisible, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  })
  return (
    <Container>
      <Header>
        <LogoContainer>
          <Logo />
        </LogoContainer>
        <Spacer size="fill" />
        <MenuButton
          {...insertTestingSelector('NavMobileMenuButton')}
          isOpen={menuVisible}
          onClick={() => setMenuVisible(!menuVisible)}
        />
      </Header>
      <Menu isVisible={menuVisible}>
        <NavbarContents company={company} user={user} onLogout={onLogout} />
      </Menu>
      {transitions(
        (style, item, _, key) =>
          item && (
            <Backdrop
              {...insertTestingSelector('NavMobileBackdrop')}
              key={key}
              style={style}
              onClick={() => setMenuVisible(false)}
            />
          )
      )}
    </Container>
  )
}

const Backdrop = styled(animated.div)`
  width: 100%;
  height: 100%;
  position: fixed;
  background-color: rgba(0, 0, 0, 0.5);
`

const Container = styled.div`
  position: relative;
  width: 100%;
  background-color: ${color.background.nav};
  z-index: 1;
`

const Header = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  padding: ${SPACING[2]} ${SPACING[4]};
  align-items: center;
`

const LogoContainer = styled.div`
  flex: 1 1 auto;
  display: flex;
  svg path {
    fill: ${color.text.inverted};
  }
  svg {
    width: 100px;
  }
`

const Menu = styled.div<{ isVisible: boolean }>`
  max-height: ${(p) => (p.isVisible ? 1000 : 0)}px;
  overflow: hidden;
  transition: max-height ${TRANSITION_TIME_MD_MS}ms ease-in;
`
